import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import IssueDescription from '@/components/shared/IssueDescription';
import { StatusBadge, PriorityBadge } from '@/components/shared/StatusBadge';
import { toast } from 'sonner';
import { usersApi, productsApi, ticketsApi } from '@/lib/api';
import useAppStore from '@/stores/appStore';
import { 
  MagnifyingGlass, 
  Plus, 
  UserCircle,
  Envelope,
  Phone,
  Package,
  Ticket,
  PlusCircle,
  CheckCircle,
  XCircle,
  FileText,
  Clock,
  ArrowLeft,
  Compass,
  MapPin,
  Check,
  Trash
} from '@phosphor-icons/react';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import { useProductCategories, useIssueCategories } from '@/hooks/useProducts';

export const AgentLookup = () => {
  const queryClient = useQueryClient();
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const currentUser = useAppStore((state) => state.currentUser);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const dashboardRole = currentUser?.role || 'agent';

  // Search State
  const [searchPhone, setSearchPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [searched, setSearched] = useState(false);

  // Admin Customer Directory State
  const [allCustomers, setAllCustomers] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  const fetchAllCustomers = async () => {
    setLoadingAll(true);
    try {
      const { data } = await usersApi.list({ role: 'customer' });
      setAllCustomers(data || []);
    } catch (error) {
      console.error('Failed to load customers:', error);
      toast.error('Failed to load customer directory');
    } finally {
      setLoadingAll(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAllCustomers();
    }
  }, [isSuperAdmin]);

  // Customer Data State
  const [products, setProducts] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // New Customer Provisioning State
  const [showRegisterCustomer, setShowRegisterCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newCustomerPhone, setNewCustomerPhone] = useState('');
  const [registeringCustomer, setRegisteringCustomer] = useState(false);

  // Product Registration State
  const [showRegisterProduct, setShowRegisterProduct] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [registeringProduct, setRegisteringProduct] = useState(false);

  // Quick Create Product State
  const [isQuickCreating, setIsQuickCreating] = useState(false);
  const [quickCategoryId, setQuickCategoryId] = useState('');
  const [quickModelName, setQuickModelName] = useState('');

  const generateSerial = () => {
    const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
    setSerialNumber(`SN-${Date.now().toString().slice(-6)}-${randomSuffix}`);
  };

  useEffect(() => {
    if (!showRegisterProduct) {
      setSerialNumber('');
      setQuickModelName('');
      setQuickCategoryId('');
      setIsQuickCreating(false);
    }
  }, [showRegisterProduct]);

  // Ticket Creation State
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketPriority, setTicketPriority] = useState('normal');
  const [ticketServiceAddress, setTicketServiceAddress] = useState('');
  const [submittingTicket, setSubmittingTicket] = useState(false);

  // Stepped Ticket Form States
  const [ticketStep, setTicketStep] = useState(1);
  const [ticketPincode, setTicketPincode] = useState('');
  const [ticketStreetAddress, setTicketStreetAddress] = useState('');
  const [ticketLocality, setTicketLocality] = useState('');
  const [ticketLandmark, setTicketLandmark] = useState('');
  const [ticketCity, setTicketCity] = useState('');
  const [ticketState, setTicketState] = useState('');
  const [ticketLat, setTicketLat] = useState(null);
  const [ticketLng, setTicketLng] = useState(null);
  const [fetchingPincode, setFetchingPincode] = useState(false);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [geocodingAddress, setGeocodingAddress] = useState(false);
  const [geocodeTimeoutId, setGeocodeTimeoutId] = useState(null);
  const [gpsPermissionDenied, setGpsPermissionDenied] = useState(false);
  const [pincodeRecords, setPincodeRecords] = useState([]);
  const [step3SubmitAttempted, setStep3SubmitAttempted] = useState(false);

  // Retrieve Categories/Issues
  const { data: categories } = useProductCategories();
  const selectedProduct = products.find(p => p.id === selectedProductId);
  const { data: issues } = useIssueCategories(selectedProduct?.category_id);

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    if (!searchPhone.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setSearching(true);
    setSearched(true);
    setCustomer(null);
    setProducts([]);
    setTickets([]);

    try {
      let targetCustomer = null;

      // If search query looks like a ticket number (starts with T-)
      if (searchPhone.toLowerCase().startsWith('t-')) {
        const { data: ticketsData } = await ticketsApi.list({ limit: 1000 });
        const matchedTicket = ticketsData.data?.find(
          t => t.ticket_number?.toLowerCase() === searchPhone.toLowerCase().trim()
        );
        if (matchedTicket) {
          const { data: userData } = await usersApi.list({ role: 'customer' });
          targetCustomer = userData.find(u => u.id === matchedTicket.customer_id);
        }
      }

      // If customer was not found via ticket search, perform standard user search
      if (!targetCustomer) {
        const { data } = await usersApi.list({ role: 'customer', search: searchPhone });
        if (data && data.length > 0) {
          targetCustomer = data[0];
        }
      }

      // Try serial number search as fallback
      if (!targetCustomer) {
        try {
          const { data: prodData } = await productsApi.getBySerial(searchPhone.trim());
          if (prodData && prodData.customer_id) {
            const { data: userData } = await usersApi.list({ role: 'customer' });
            targetCustomer = userData.find(u => u.id === prodData.customer_id);
            if (targetCustomer) {
              toast.success(`Found customer associated with serial number: ${prodData.serial_number}`);
            }
          }
        } catch (err) {
          // Serial not found, ignore
        }
      }

      if (targetCustomer) {
        setCustomer(targetCustomer);
        await loadCustomerDetails(targetCustomer.id);
      } else {
        toast.info('No customer account found. You can register them below.');
        setShowRegisterCustomer(true);
        setNewCustomerName('');
        setNewCustomerEmail('');
        setNewCustomerPhone(searchPhone);
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Error searching for caller profile');
    } finally {
      setSearching(false);
    }
  };

  const loadCustomerDetails = async (customerId) => {
    setLoadingDetails(true);
    try {
      // Load products
      const productsRes = await productsApi.getCustomerProducts(customerId);
      setProducts(productsRes.data || []);

      // Load tickets
      const ticketsRes = await ticketsApi.list({ customer_id: customerId });
      setTickets(ticketsRes.data?.data || ticketsRes.data || []);
    } catch (error) {
      console.error('Failed to load customer details:', error);
      toast.error('Failed to retrieve registered products/tickets');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRegisterCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomerName) {
      toast.error('Customer name is required');
      return;
    }

    let trimmedPhone = newCustomerPhone.trim();
    if (/^\d{10}$/.test(trimmedPhone)) {
      trimmedPhone = `+91${trimmedPhone}`;
      setNewCustomerPhone(trimmedPhone);
    }
    if (!trimmedPhone.startsWith('+')) {
      toast.error('Phone number must start with "+" and include country code (e.g., +91)');
      return;
    }
    if (!/^\+[1-9]\d{1,14}$/.test(trimmedPhone)) {
      toast.error('Phone number must be in E.164 format (e.g., +919876543210)');
      return;
    }

    setRegisteringCustomer(true);
    try {
      const payload = {
        full_name: newCustomerName,
        phone: trimmedPhone,
        role: 'customer',
      };
      if (newCustomerEmail) payload.email = newCustomerEmail;

      const { data } = await usersApi.create(payload);
      setCustomer(data);
      toast.success('Customer registered successfully!');
      setShowRegisterCustomer(false);
      if (isSuperAdmin) {
        await fetchAllCustomers();
      }
      await loadCustomerDetails(data.id);
    } catch (error) {
      console.error('Customer registration failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create customer account');
    } finally {
      setRegisteringCustomer(false);
    }
  };

  const handleRegisterProduct = async (e) => {
    e.preventDefault();
    if (!serialNumber.trim()) {
      toast.error('Serial number is required');
      return;
    }

    setRegisteringProduct(true);
    try {
      if (isQuickCreating) {
        if (!quickCategoryId) {
          toast.error('Category is required');
          setRegisteringProduct(false);
          return;
        }
        if (!quickModelName.trim()) {
          toast.error('Model name is required');
          setRegisteringProduct(false);
          return;
        }
        
        // 1. Create product in registry
        await productsApi.createGlobal({
          category_id: quickCategoryId,
          serial_number: serialNumber.trim(),
          model_name: quickModelName.trim(),
        });
      }

      // 2. Register for customer
      await productsApi.registerForCustomer(customer.id, serialNumber.trim());
      toast.success('Product registered successfully for customer');
      setSerialNumber('');
      setQuickModelName('');
      setQuickCategoryId('');
      setIsQuickCreating(false);
      setShowRegisterProduct(false);
      await loadCustomerDetails(customer.id);
    } catch (error) {
      console.error('Product registration failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to register product');
    } finally {
      setRegisteringProduct(false);
    }
  };

  const handleUnregisterProduct = async (registrationId, productName) => {
    if (!window.confirm(`Are you sure you want to remove the registration for "${productName}" from this customer's account?`)) {
      return;
    }
    try {
      await productsApi.unregisterProduct(registrationId);
      toast.success(`Removed registration for ${productName}`);
      if (customer) {
        await loadCustomerDetails(customer.id);
      }
    } catch (error) {
      console.error('Failed to unregister product:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove registration');
    }
  };

  useEffect(() => {
    if (!showCreateTicket) {
      setTicketStep(1);
      setSelectedProductId('');
      setSelectedCategoryIds([]);
      setTicketDescription('');
      setTicketPriority('normal');
      setTicketServiceAddress('');
      setTicketPincode('');
      setTicketStreetAddress('');
      setTicketLocality('');
      setTicketLandmark('');
      setTicketCity('');
      setTicketState('');
      setTicketLat(null);
      setTicketLng(null);
      setPincodeRecords([]);
      setStep3SubmitAttempted(false);
    }
  }, [showCreateTicket]);

  useEffect(() => {
    return () => {
      if (geocodeTimeoutId) clearTimeout(geocodeTimeoutId);
    };
  }, [geocodeTimeoutId]);

  const fetchPincodeData = async (pin) => {
    const cacheKey = `pincode_${pin}`;
    const cachedStr = localStorage.getItem(cacheKey);
    if (cachedStr) {
      try {
        const cached = JSON.parse(cachedStr);
        if (Date.now() - cached.timestamp < 24 * 60 * 60 * 1000) {
          return cached.records;
        }
      } catch (e) {
        console.warn('Failed to parse cached pincode data', e);
      }
    }

    const API_KEY = "579b464db66ec23bdd000001f06cf0cb978841b27a139f73a62a9c8a";
    const url = `https://api.data.gov.in/resource/5c2f62fe-5afa-4119-a499-fec9d604d5bd?api-key=${API_KEY}&format=json&filters[pincode]=${pin}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);

    try {
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (!res.ok) throw new Error('Failed to fetch pincode details from Gov API');
      const data = await res.json();
      const records = data.records || [];
      
      if (records.length > 0) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            records,
            timestamp: Date.now()
          }));
        } catch (e) {
          console.warn('Failed to store pincode cache', e);
        }
      }
      return records;
    } catch (e) {
      clearTimeout(timeoutId);
      throw e;
    }
  };

  const triggerPincodeLookup = async (pin) => {
    setFetchingPincode(true);
    try {
      const records = await fetchPincodeData(pin);
      setPincodeRecords(records);
      if (records && records.length > 0) {
        const first = records[0];
        const toTitleCase = (str) => str ? str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.substring(1)).join(' ') : '';
        const cleanState = toTitleCase(first.statename);
        const cleanCity = toTitleCase(first.district);
        
        setTicketState(cleanState);
        setTicketCity(cleanCity);
        setTicketPincode(pin);

        if (first.latitude && first.latitude !== 'NA' && first.longitude && first.longitude !== 'NA') {
          setTicketLat(parseFloat(first.latitude));
          setTicketLng(parseFloat(first.longitude));
        }
        
        const combined = updateCombinedAddress({
          streetAddress: ticketStreetAddress,
          locality: ticketLocality,
          landmark: ticketLandmark,
          city: cleanCity,
          state: cleanState,
          pincode: pin
        });
        setTicketServiceAddress(combined);
        toast.success('Pincode details loaded successfully!');
      } else {
        toast.warning('Pincode details not found in government database.');
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.warn('Pincode lookup timed out');
      } else {
        console.error(e);
        toast.error('Could not load pincode details.');
      }
    } finally {
      setFetchingPincode(false);
    }
  };

  const updateCombinedAddress = (fields) => {
    const { streetAddress, locality, landmark, city, state, pincode } = fields;
    const parts = [
      streetAddress?.trim(),
      locality?.trim(),
      landmark?.trim() ? `Landmark: ${landmark.trim()}` : null,
      city?.trim(),
      state?.trim(),
      pincode?.trim()
    ].filter(Boolean);
    return parts.join(', ');
  };

  const geocodeAddress = async (updatedFields) => {
    const { streetAddress, city, state, pincode } = updatedFields;
    if (!city?.trim() || !state?.trim() || !pincode?.trim()) {
      return;
    }

    const queryParts = [streetAddress?.trim(), city.trim(), state.trim(), pincode.trim()].filter(Boolean);
    const query = queryParts.join(', ');

    setGeocodingAddress(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
        {
          headers: {
            'Accept-Language': 'en',
            'User-Agent': 'KwiccApp/1.0'
          }
        }
      );
      if (!response.ok) throw new Error('OSM Geocoding failed');
      const data = await response.json();
      if (data && data.length > 0) {
        setTicketLat(parseFloat(data[0].lat));
        setTicketLng(parseFloat(data[0].lon));
      }
    } catch (error) {
      console.error('Failed to geocode address:', error);
    } finally {
      setGeocodingAddress(false);
    }
  };

  const handleAddressFieldChange = (field, value) => {
    const updatedFields = {
      streetAddress: ticketStreetAddress,
      locality: ticketLocality,
      landmark: ticketLandmark,
      city: ticketCity,
      state: ticketState,
      pincode: ticketPincode
    };

    if (field === 'streetAddress') {
      setTicketStreetAddress(value);
      updatedFields.streetAddress = value;
    } else if (field === 'locality') {
      setTicketLocality(value);
      updatedFields.locality = value;
    } else if (field === 'landmark') {
      setTicketLandmark(value);
      updatedFields.landmark = value;
    } else if (field === 'city') {
      setTicketCity(value);
      updatedFields.city = value;
    } else if (field === 'state') {
      setTicketState(value);
      updatedFields.state = value;
    } else if (field === 'pincode') {
      setTicketPincode(value);
      updatedFields.pincode = value;
    }

    const combined = updateCombinedAddress(updatedFields);
    setTicketServiceAddress(combined);

    if (field === 'pincode' && value.length === 6 && /^\d+$/.test(value)) {
      triggerPincodeLookup(value);
    }

    if (geocodeTimeoutId) {
      clearTimeout(geocodeTimeoutId);
    }
    const newTimeoutId = setTimeout(() => {
      geocodeAddress(updatedFields);
    }, 1000);
    setGeocodeTimeoutId(newTimeoutId);
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser.');
      return;
    }

    setDetectingLocation(true);
    const toastId = toast.loading('Detecting location...');

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setGpsPermissionDenied(false);
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        setTicketLat(lat);
        setTicketLng(lng);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            {
              headers: {
                'Accept-Language': 'en',
                'User-Agent': 'KwiccApp/1.0'
              },
              signal: controller.signal
            }
          );
          clearTimeout(timeoutId);
          if (!response.ok) throw new Error('OSM Reverse Geocoding failed');
          
          const data = await response.json();
          const addressObj = data.address || {};
          
          const postcode = addressObj.postcode || '';
          const city = addressObj.city || addressObj.town || addressObj.village || addressObj.suburb || '';
          const state = addressObj.state || '';
          const street = [
            addressObj.house_number,
            addressObj.road,
            addressObj.neighbourhood
          ].filter(Boolean).join(', ') || addressObj.suburb || '';
          
          setTicketPincode(postcode || ticketPincode);
          setTicketCity(city || ticketCity);
          setTicketState(state || ticketState);
          setTicketStreetAddress(street || ticketStreetAddress);

          const updatedFields = {
            streetAddress: street || ticketStreetAddress,
            locality: ticketLocality,
            landmark: ticketLandmark,
            city: city || ticketCity,
            state: state || ticketState,
            pincode: postcode || ticketPincode
          };
          
          const combined = updateCombinedAddress(updatedFields) || data.display_name;
          setTicketServiceAddress(combined);
          
          if (postcode) {
            triggerPincodeLookup(postcode);
          }
          
          toast.success('Location detected and address form pre-filled!', { id: toastId });
        } catch (error) {
          console.error(error);
          setTicketServiceAddress(prev => prev || `Latitude: ${lat}, Longitude: ${lng}`);
          toast.success(`Coordinates mapped, but address details could not be resolved. Please type details manually.`, { id: toastId });
        } finally {
          setDetectingLocation(false);
        }
      },
      (error) => {
        console.error(error);
        let errorMsg = 'Failed to detect location';
        if (error.code === 3) {
          errorMsg = 'Location request timed out. Please enter address details manually.';
        } else if (error.code === 1) {
          setGpsPermissionDenied(true);
          errorMsg = 'Location permission denied. Please enter address details manually.';
        } else if (error.code === 2) {
          errorMsg = 'Location unavailable. Please enter address details manually.';
        }

        if (pincodeRecords && pincodeRecords.length > 0) {
          const first = pincodeRecords[0];
          if (first.latitude && first.latitude !== 'NA' && first.longitude && first.longitude !== 'NA') {
            const fallbackLat = parseFloat(first.latitude);
            const fallbackLng = parseFloat(first.longitude);
            setTicketLat(fallbackLat);
            setTicketLng(fallbackLng);
            toast.info('Using coordinates from pincode directory as fallback.', { id: toastId });
            setDetectingLocation(false);
            return;
          }
        }

        toast.error(errorMsg, { id: toastId });
        setDetectingLocation(false);
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
    );
  };

  const handleNextStep = () => {
    if (ticketStep === 1) {
      if (!selectedProductId) {
        toast.error('Please select a product');
        return;
      }
      const prod = products?.find(p => p.id === selectedProductId);
      if (prod && prod.warranty_status === 'Expired' && prod.allow_out_of_warranty_tickets === false) {
        toast.error('Raising service tickets is disabled for this out-of-warranty product.');
        return;
      }
    }
    if (ticketStep === 2) {
      if (selectedCategoryIds.length === 0) {
        toast.error('Please select at least one issue category');
        return;
      }
    }
    if (ticketStep === 3) {
      setStep3SubmitAttempted(true);
      if (!ticketStreetAddress?.trim() || !ticketCity?.trim() || !ticketState?.trim() || !ticketPincode?.trim()) {
        toast.error('Street Address, City, State, and Pincode are mandatory');
        return;
      }
      setStep3SubmitAttempted(false);
    }
    setTicketStep((prev) => prev + 1);
  };

  const handleCreateTicket = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!selectedProductId) {
      toast.error('Please select a product');
      return;
    }
    const prod = products?.find(p => p.id === selectedProductId);
    if (prod && prod.warranty_status === 'Expired' && prod.allow_out_of_warranty_tickets === false) {
      toast.error('Raising service tickets is disabled for this out-of-warranty product.');
      return;
    }
    if (selectedCategoryIds.length === 0) {
      toast.error('Please select at least one issue category');
      return;
    }
    if (!ticketStreetAddress?.trim() || !ticketCity?.trim() || !ticketState?.trim() || !ticketPincode?.trim()) {
      setStep3SubmitAttempted(true);
      toast.error('Street Address, City, State, and Pincode are mandatory');
      return;
    }

    setSubmittingTicket(true);
    try {
      const selectedIssueLabels = selectedCategoryIds.map(id => {
        const issue = issues?.find(i => i.id === id);
        return issue ? issue.label : '';
      }).filter(Boolean);

      const compiledDescription = `Selected Issues:\n${selectedIssueLabels.map(l => `• ${l}`).join('\n')}\n\nCaller Description:\n${ticketDescription}`;

      const payload = {
        product_id: selectedProductId,
        issue_category_id: selectedCategoryIds[0],
        description: compiledDescription,
        priority: ticketPriority,
        customer_id: customer.id,
        service_address: ticketServiceAddress.trim(),
        customer_lat: ticketLat,
        customer_lng: ticketLng,
      };

      await ticketsApi.create(payload);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success('Service ticket created and automatically approved!');
      
      // Reset form & reload details
      setShowCreateTicket(false);
      await loadCustomerDetails(customer.id);
    } catch (error) {
      console.error('Ticket creation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to raise service ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const filteredCustomers = allCustomers.filter(c =>
    c.full_name?.toLowerCase().includes(searchPhone.toLowerCase()) ||
    c.phone?.includes(searchPhone) ||
    (c.email && c.email.toLowerCase().includes(searchPhone.toLowerCase())) ||
    (c.registered_serials && c.registered_serials.some(s => s.toLowerCase().includes(searchPhone.toLowerCase())))
  );

  return (
    <DashboardLayout role={dashboardRole}>
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black tracking-tight mb-2">Caller Search & Lookup</h1>
            <p className="text-muted-foreground">Search customer profiles by phone number, check serials, and log auto-approved tickets</p>
          </div>
          {customer && (
            <Button
              onClick={() => {
                setCustomer(null);
                setSearched(false);
                setSearchPhone('');
              }}
              variant="outline"
              className="flex items-center gap-2 border-slate-300 hover:bg-slate-50 font-bold"
            >
              <ArrowLeft size={16} weight="bold" />
              Back to Directory
            </Button>
          )}
        </div>

        {/* Search Panel */}
        {!customer && (
          <Card className="p-6 border shadow-sm bg-white">
            <form onSubmit={handleSearch} className="flex gap-3 max-w-2xl">
              <div className="relative flex-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                <Input
                  placeholder={isSuperAdmin ? "Search by customer name, email, phone, ticket, or serial number..." : "Enter customer phone, name, email, ticket, or serial number..."}
                  value={searchPhone}
                  onChange={(e) => setSearchPhone(e.target.value)}
                  className="pl-10 h-12 text-base font-medium border-slate-300 focus:border-primary"
                  data-testid="caller-phone-input"
                />
              </div>
              <Button
                type="submit"
                disabled={searching}
                className="h-12 px-6 font-bold text-white shadow-sm flex items-center gap-2"
                style={{ backgroundColor: brandColorAccent }}
                data-testid="caller-search-btn"
              >
                {searching ? (
                  <LoadingSpinner className="h-5 w-5" />
                ) : (
                  <>
                    <MagnifyingGlass size={20} weight="bold" />
                    {isSuperAdmin ? "Filter / Search" : "Search Caller"}
                  </>
                )}
              </Button>
            </form>

            {/* Register new customer shortcut */}
            {!customer && !searched && (
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-500">Customer not registered?</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="font-bold text-xs h-8 px-3"
                  onClick={() => {
                    setShowRegisterCustomer(true);
                    setNewCustomerName('');
                    setNewCustomerEmail('');
                    setNewCustomerPhone(searchPhone);
                  }}
                  data-testid="register-customer-shortcut-btn"
                >
                  <Plus size={14} className="mr-1" />
                  Register New Customer
                </Button>
              </div>
            )}
          </Card>
        )}

        {searching && (
          <div className="py-12 flex justify-center">
            <LoadingSpinner />
          </div>
        )}

        {/* Admin customer list */}
        {isSuperAdmin && !customer && !searching && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                <UserCircle size={28} className="text-slate-500" />
                Customer Directory ({filteredCustomers.length})
              </h2>
            </div>
            
            {loadingAll ? (
              <LoadingSpinner />
            ) : filteredCustomers.length > 0 ? (
              <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100 text-xs font-black uppercase tracking-wider text-slate-500">
                        <th className="py-4 px-6">Name</th>
                        <th className="py-4 px-6">Phone Number</th>
                        <th className="py-4 px-6">Email Address</th>
                        <th className="py-4 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-sm">
                      {filteredCustomers.map((cust) => (
                        <tr key={cust.id} className="hover:bg-slate-50/50 transition-colors">
                          <td className="py-4 px-6 font-bold text-slate-800 flex items-center gap-2">
                            <UserCircle size={22} className="text-slate-400" />
                            {cust.full_name}
                          </td>
                          <td className="py-4 px-6 font-mono font-medium text-slate-700">{cust.phone}</td>
                          <td className="py-4 px-6 text-slate-500">{cust.email || '—'}</td>
                          <td className="py-4 px-6 text-right">
                            <Button
                              onClick={() => {
                                setCustomer(cust);
                                setSearched(true);
                                loadCustomerDetails(cust.id);
                              }}
                              size="sm"
                              className="font-bold text-xs uppercase tracking-wider text-white"
                              style={{ backgroundColor: brandColorAccent }}
                            >
                              Lookup Profile
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <Card className="p-12 text-center border bg-slate-50/20 border-dashed">
                <XCircle size={44} className="mx-auto text-slate-300 mb-3" />
                <p className="text-sm font-semibold text-slate-500">No customers found in directory.</p>
                {searchPhone.trim() && (
                  <Button
                    onClick={() => {
                      setNewCustomerName('');
                      setNewCustomerEmail('');
                      setShowRegisterCustomer(true);
                    }}
                    className="mt-4 font-bold text-white text-xs"
                    style={{ backgroundColor: brandColorAccent }}
                  >
                    Register Customer "{searchPhone}"
                  </Button>
                )}
              </Card>
            )}
          </div>
        )}

        {/* Customer Profile & Sub-sections */}
        {!searching && customer && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Customer Details & Actions */}
            <div className="space-y-6">
              <Card className="p-6 border shadow-sm bg-white relative overflow-hidden">
                <div className="absolute top-0 left-0 w-2 h-full" style={{ backgroundColor: brandColorAccent }}></div>
                <div className="flex items-center gap-4 mb-6">
                  <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center font-bold text-lg text-slate-600 shadow-sm border border-slate-200">
                    <UserCircle size={36} className="text-slate-500" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight">{customer.full_name}</h2>
                    <p className="text-sm font-bold text-slate-500 flex items-center gap-1.5 uppercase tracking-wider mt-1">
                      <span className="h-2 w-2 rounded-full bg-emerald-500"></span> Customer Profile
                    </p>
                  </div>
                </div>

                <div className="space-y-4 text-sm border-t border-slate-100 pt-4">
                  <div>
                    <Label className="label-micro block mb-1">Phone Number</Label>
                    <p className="font-mono font-semibold text-slate-800 flex items-center gap-1">
                      <Phone size={14} className="text-slate-400" /> {customer.phone}
                    </p>
                  </div>
                  <div>
                    <Label className="label-micro block mb-1">Email Address</Label>
                    <p className="font-semibold text-slate-800 flex items-center gap-1">
                      <Envelope size={14} className="text-slate-400" /> {customer.email || '—'}
                    </p>
                  </div>
                </div>

                <div className="mt-8 pt-4 border-t border-slate-100 flex flex-col gap-3">
                  <Button
                    onClick={() => {
                      setSelectedProductId('');
                      setSelectedCategoryIds([]);
                      setTicketDescription('');
                      setTicketPriority('normal');
                      setTicketServiceAddress('');
                      setShowCreateTicket(true);
                    }}
                    className="w-full font-bold text-white shadow-md flex items-center justify-center gap-2 py-5"
                    style={{ backgroundColor: brandColorAccent }}
                    data-testid="raise-ticket-btn"
                  >
                    <PlusCircle size={20} weight="bold" />
                    Raise Service Ticket
                  </Button>

                  <Button
                    onClick={() => {
                      setSerialNumber('');
                      setShowRegisterProduct(true);
                    }}
                    variant="outline"
                    className="w-full font-bold border-slate-300 hover:bg-slate-50 flex items-center justify-center gap-2 py-5"
                    data-testid="add-product-btn"
                  >
                    <Plus size={18} weight="bold" />
                    Register Customer Product
                  </Button>
                </div>
              </Card>
            </div>

            {/* Products and Tickets Lists */}
            <div className="lg:col-span-2 space-y-8">
              {/* Registered Products */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-black text-slate-800 tracking-tight flex items-center gap-2">
                    <Package size={22} className="text-slate-500" />
                    Registered Products ({products.length})
                  </h3>
                </div>

                {loadingDetails ? (
                  <LoadingSpinner />
                ) : products.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {products.map((product) => (
                      <Card key={product.id} className="p-5 border shadow-sm bg-white hover:border-slate-300 transition-all">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-slate-50 border rounded text-slate-500">
                            <Package size={20} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold text-slate-800 truncate mb-1">{product.product_name}</h4>
                            <p className="text-xs text-muted-foreground truncate mb-3">Category: {product.category_name}</p>
                            
                            <div className="space-y-1.5 text-xs">
                              <div>
                                <span className="text-slate-400 font-medium">Serial:</span>{' '}
                                <span className="font-mono font-bold text-slate-800">{product.serial_number}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <span className="text-slate-400 font-medium">Warranty:</span>
                                <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-black uppercase ${
                                  product.warranty_status === 'Active' 
                                    ? 'bg-green-100 text-green-800' 
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                  {product.warranty_status}
                                </span>
                              </div>
                            </div>
                            {product.registration_id && (
                              <Button
                                onClick={() => handleUnregisterProduct(product.registration_id, product.product_name || product.serial_number)}
                                variant="outline"
                                size="sm"
                                className="mt-3 w-full font-bold text-[10px] uppercase tracking-wider h-8 text-red-650 border-red-200 hover:bg-red-50 hover:text-red-700 flex items-center justify-center gap-1.5"
                                data-testid={`unregister-btn-${product.serial_number}`}
                              >
                                <Trash size={14} /> Remove Registration
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card className="p-8 text-center border-dashed bg-slate-50/50">
                    <Package size={32} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm font-semibold text-slate-500">No products registered under this profile.</p>
                  </Card>
                )}
              </div>

              {/* Service/Ticket History */}
              <div>
                <h3 className="text-xl font-black text-slate-800 tracking-tight flex items-center gap-2 mb-4">
                  <Ticket size={22} className="text-slate-500" />
                  Service History ({tickets.length})
                </h3>

                {loadingDetails ? (
                  <LoadingSpinner />
                ) : tickets.length > 0 ? (
                  <div className="space-y-3">
                    {tickets.map((ticket) => (
                      <Link key={ticket.id} to={isSuperAdmin ? `/ops/admin/tickets/${ticket.id}` : `/ops/agent/tickets/${ticket.id}`}>
                        <Card className="p-4 border shadow-sm bg-white hover:bg-slate-50/30 transition-all cursor-pointer">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-mono font-black text-slate-800">{ticket.ticket_number}</span>
                                <StatusBadge status={ticket.status} />
                                <PriorityBadge priority={ticket.priority} />
                              </div>
                              <p className="text-sm font-bold text-slate-700 mt-1">{ticket.product_name}</p>
                              <div className="mt-1">
                                <IssueDescription description={ticket.description} compact />
                              </div>
                            </div>
                            <div className="text-left sm:text-right text-xs text-slate-400 font-semibold flex items-center gap-1 sm:justify-end">
                              <Clock size={14} />
                              {formatRelativeTime(ticket.created_at)}
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Card className="p-8 text-center border-dashed bg-slate-50/50">
                    <Ticket size={32} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-sm font-semibold text-slate-500">No service ticket history exists.</p>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Not Found View */}
        {!isSuperAdmin && !searching && searched && !customer && !showRegisterCustomer && (
          <Card className="p-12 text-center max-w-xl mx-auto border shadow-sm bg-white">
            <XCircle size={48} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-bold mb-2">No Customer Profile Found</h2>
            <p className="text-muted-foreground mb-6">We couldn't find an account matching phone number "{searchPhone}".</p>
            <Button
              onClick={() => {
                setNewCustomerName('');
                setNewCustomerEmail('');
                setNewCustomerPhone(searchPhone);
                setShowRegisterCustomer(true);
              }}
              className="font-bold text-white"
              style={{ backgroundColor: brandColorAccent }}
            >
              <Plus size={18} className="mr-1.5" />
              Register Customer Account
            </Button>
          </Card>
        )}

        {/* Provision Customer Account Modal */}
        <Dialog open={showRegisterCustomer} onOpenChange={setShowRegisterCustomer}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Register New Customer</DialogTitle>
              <DialogDescription className="text-xs">
                Create a customer user account inline to support ticket tracking and alerts.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleRegisterCustomer} className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label htmlFor="cust-name" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Customer Full Name *
                </Label>
                <Input
                  id="cust-name"
                  placeholder="Enter customer's name"
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  className="h-11 border-slate-300"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cust-phone" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Phone Number *
                </Label>
                <Input
                  id="cust-phone"
                  placeholder="Enter phone number (e.g. +919876543210)"
                  value={newCustomerPhone}
                  onChange={(e) => setNewCustomerPhone(e.target.value)}
                  className="h-11 border-slate-300 font-mono"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="cust-email" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Email Address (Optional)
                </Label>
                <Input
                  id="cust-email"
                  type="email"
                  placeholder="customer@domain.com"
                  value={newCustomerEmail}
                  onChange={(e) => setNewCustomerEmail(e.target.value)}
                  className="h-11 border-slate-300"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowRegisterCustomer(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={registeringCustomer}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {registeringCustomer ? 'Saving...' : 'Register Customer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Register Product Modal */}
        <Dialog open={showRegisterProduct} onOpenChange={setShowRegisterProduct}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Register Product</DialogTitle>
              <DialogDescription className="text-xs">
                Associate a physical serial number with the customer's account directory.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleRegisterProduct} className="space-y-4 mt-4">
              <div className="flex justify-between items-center border-b pb-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Product Details
                </Label>
                <button
                  type="button"
                  onClick={() => setIsQuickCreating(!isQuickCreating)}
                  className="text-xs font-bold text-primary hover:underline"
                >
                  {isQuickCreating ? 'Use Existing Registry' : 'Quick Create Product'}
                </button>
              </div>

              {isQuickCreating && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                      Product Category *
                    </Label>
                    <Select value={quickCategoryId} onValueChange={setQuickCategoryId}>
                      <SelectTrigger className="h-11 border-slate-300 bg-white">
                        <SelectValue placeholder="Select Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories?.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="model-name" className="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                      Model Name *
                    </Label>
                    <Input
                      id="model-name"
                      placeholder="e.g. Standard Model Refrigerator"
                      value={quickModelName}
                      onChange={(e) => setQuickModelName(e.target.value)}
                      className="h-11 border-slate-300"
                      required={isQuickCreating}
                    />
                  </div>
                </>
              )}

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="serial" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Product Serial Number *
                  </Label>
                  <button
                    type="button"
                    onClick={generateSerial}
                    className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1"
                  >
                    Generate Serial
                  </button>
                </div>
                <Input
                  id="serial"
                  placeholder="Enter serial number (e.g. SC-2023-XYZ)..."
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  className="h-11 border-slate-300 font-mono"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowRegisterProduct(false)}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={registeringProduct}
                  className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                  style={{ backgroundColor: brandColorAccent }}
                >
                  {registeringProduct ? 'Registering...' : isQuickCreating ? 'Create & Add' : 'Add Product'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Raise Service Ticket Modal */}
        <Dialog open={showCreateTicket} onOpenChange={setShowCreateTicket}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">Raise Service Ticket</DialogTitle>
              <DialogDescription className="text-xs">
                Log a new service issue for this customer. It will bypass customer review and be auto-approved.
              </DialogDescription>
            </DialogHeader>

            {/* Progress Bar */}
            <div className="my-4">
              <div className="flex gap-2">
                {[1, 2, 3, 4].map((s) => (
                  <div
                    key={s}
                    className={`h-2 flex-1 rounded-full transition-all ${
                      s <= ticketStep ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2 text-right">Step {ticketStep} of 4</p>
            </div>

            <form 
              onSubmit={(e) => {
                e.preventDefault();
                if (ticketStep === 4) {
                  handleCreateTicket(e);
                }
              }} 
              className="space-y-4"
            >
              {/* Step 1: Select Product */}
              {ticketStep === 1 && (
                <div className="space-y-4 animate-fade-in">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-2">
                    Select Customer Product *
                  </Label>
                  {products.length > 0 ? (
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                      {products.map((product) => {
                        const isWarrantyExpired = product.warranty_status === 'Expired';
                        const isLockedOut = isWarrantyExpired && product.allow_out_of_warranty_tickets === false;

                        return (
                          <Card 
                            key={product.id} 
                            className={`p-3.5 transition-all border flex items-center justify-between ${
                              selectedProductId === product.id ? 'border-primary ring-2 ring-primary bg-primary/5' : ''
                            } ${isLockedOut ? 'opacity-65 bg-slate-50 border-slate-200 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'}`}
                            onClick={() => {
                              if (isLockedOut) {
                                toast.error("Raising tickets for this product is blocked because its warranty has expired.");
                                return;
                              }
                              setSelectedProductId(product.id);
                              setSelectedCategoryIds([]);
                            }}
                          >
                            <div className="flex-1">
                              <div className="flex justify-between items-start mr-2">
                                <p className="font-semibold text-sm text-slate-800">{product.product_name || product.model_name}</p>
                                {isLockedOut && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-red-650 px-2 py-0.5 rounded bg-red-50 border border-red-200">
                                    Service Blocked
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">Serial: {product.serial_number}</p>
                              <p className="text-[10.5px] text-muted-foreground mt-0.5">
                                {product.category_name || 'General Product'} • Warranty: {product.warranty_status || 'Checking...'}
                              </p>
                              {isLockedOut && (
                                <p className="text-[11px] text-red-655 font-semibold mt-1">
                                  Warranty has expired and service requests are disabled for this model.
                                </p>
                              )}
                            </div>
                            {selectedProductId === product.id && !isLockedOut && (
                              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-white">
                                <Check size={12} weight="bold" />
                              </div>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 border rounded bg-slate-50">
                      <p className="text-xs text-muted-foreground mb-3">No products registered for this customer.</p>
                      <Button type="button" size="sm" onClick={() => { setShowCreateTicket(false); setShowRegisterProduct(true); }} className="font-bold text-xs uppercase tracking-wider">
                        Register a Product First
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Issue & Description */}
              {ticketStep === 2 && (
                <div className="space-y-5 animate-fade-in">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Select Issue Categories * (Select all that apply)
                    </Label>
                    {issues && issues.length > 0 ? (
                      <div className="grid grid-cols-2 gap-2 border rounded-md p-3 bg-slate-50 max-h-48 overflow-y-auto">
                        {issues.map((issue) => (
                          <label key={issue.id} className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer p-1.5 hover:bg-slate-100 rounded">
                            <Checkbox 
                              checked={selectedCategoryIds.includes(issue.id)}
                              onCheckedChange={(checked) => {
                                setSelectedCategoryIds(prev => 
                                  checked 
                                    ? [...prev, issue.id] 
                                    : prev.filter(id => id !== issue.id)
                                );
                              }}
                            />
                            {issue.label}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground italic p-2 border rounded bg-slate-50">No specific issue categories found. Please describe in detail.</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Ticket Priority
                    </Label>
                    <div className="flex gap-2">
                      {['low', 'normal', 'urgent'].map((prio) => (
                        <Button
                          key={prio}
                          type="button"
                          variant={ticketPriority === prio ? 'default' : 'outline'}
                          onClick={() => setTicketPriority(prio)}
                          className={`flex-1 text-xs font-bold uppercase tracking-wider h-10 ${
                            ticketPriority === prio && prio === 'urgent'
                              ? 'bg-red-600 hover:bg-red-700 text-white'
                              : ticketPriority === prio && prio === 'normal'
                              ? 'bg-blue-600 hover:bg-blue-700 text-white'
                              : ticketPriority === prio && prio === 'low'
                              ? 'bg-slate-600 hover:bg-slate-700 text-white'
                              : ''
                          }`}
                        >
                          {prio}
                        </Button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="desc" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Issue Details & Description (Optional)
                    </Label>
                    <Textarea
                      id="desc"
                      placeholder="Describe the problem reported by the caller in detail..."
                      value={ticketDescription}
                      onChange={(e) => setTicketDescription(e.target.value)}
                      rows={5}
                      className="border-slate-300 resize-none text-slate-800"
                    />
                    <p className="text-[10px] text-slate-400 font-semibold text-right">
                      {ticketDescription.length} characters
                    </p>
                  </div>
                </div>
              )}

              {/* Step 3: Address & GPS */}
              {ticketStep === 3 && (
                <div className="space-y-5 animate-fade-in">
                  {gpsPermissionDenied && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-[10px] text-amber-800 space-y-1 mb-4" data-testid="gps-denied-help">
                      <p className="font-bold flex items-center gap-1">
                        <span>⚠️</span> Geolocation Permission Blocked
                      </p>
                      <p className="leading-normal font-medium">
                        Your browser is currently blocked from accessing your location. To allow:
                      </p>
                      <ol className="list-decimal pl-4 space-y-1 font-medium">
                        <li>Click the <strong>lock/settings icon</strong> 🔒 in the URL address bar.</li>
                        <li>Change the <strong>Location</strong> setting to <strong>Allow</strong>.</li>
                        <li><strong>Reload</strong> and click "Detect GPS Location" again.</li>
                      </ol>
                    </div>
                  )}

                  {/* Address Form Section */}
                  <div className="space-y-3.5">
                    <h4 className="text-xs font-bold text-slate-850 border-b pb-1.5">Service Address Details</h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {/* Pincode */}
                      <div className="space-y-1">
                        <Label htmlFor="pincode" className="text-[10px] font-bold uppercase tracking-wider text-slate-700 flex justify-between items-center">
                          <span>Pincode / Postal Code *</span>
                          {ticketLat && ticketLng && (
                            <span className="text-[9px] font-bold text-emerald-600 bg-emerald-50 px-1 rounded flex items-center gap-0.5">
                              ● GPS Mapped
                            </span>
                          )}
                        </Label>
                        <div className="relative flex items-center">
                          <Input
                            id="pincode"
                            placeholder="e.g. 504273"
                            value={ticketPincode}
                            onChange={(e) => handleAddressFieldChange('pincode', e.target.value)}
                            className={`h-10 border-slate-300 font-mono text-xs pr-16 ${step3SubmitAttempted && !ticketPincode?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                            maxLength={6}
                            required
                          />
                          <div className="absolute right-1.5 flex items-center gap-1">
                            {fetchingPincode && (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent mr-1.5" />
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={detectLocation}
                              disabled={detectingLocation}
                              className="h-7 px-2 text-[10px] font-bold text-primary hover:text-primary-dark hover:bg-slate-100 flex items-center gap-1"
                            >
                              <Compass size={12} className={detectingLocation ? "animate-spin" : ""} />
                              {detectingLocation ? '...' : 'Auto'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Locality Dropdown / Input */}
                      <div className="space-y-1">
                        <Label htmlFor="locality" className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
                          Locality / Post Office
                        </Label>
                        {pincodeRecords && pincodeRecords.length > 0 ? (
                          <Select 
                            value={ticketLocality} 
                            onValueChange={(val) => handleAddressFieldChange('locality', val)}
                          >
                            <SelectTrigger className="h-10 border-slate-300 bg-white text-xs">
                              <SelectValue placeholder="Select locality" />
                            </SelectTrigger>
                            <SelectContent className="bg-white border text-xs">
                              {pincodeRecords.map((rec) => (
                                <SelectItem key={rec.officename} value={rec.officename}>
                                  {rec.officename}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            id="locality"
                            placeholder="e.g. Kothimir"
                            value={ticketLocality}
                            onChange={(e) => handleAddressFieldChange('locality', e.target.value)}
                            className="h-10 border-slate-300 text-xs"
                          />
                        )}
                      </div>

                      {/* Street Address */}
                      <div className="space-y-1 sm:col-span-2">
                        <Label htmlFor="streetAddress" className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
                          Street Address / House No. / Apartment *
                        </Label>
                        <Input
                          id="streetAddress"
                          placeholder="e.g. Flat 402, Building A, Main Road"
                          value={ticketStreetAddress}
                          onChange={(e) => handleAddressFieldChange('streetAddress', e.target.value)}
                          className={`h-10 border-slate-300 text-xs ${step3SubmitAttempted && !ticketStreetAddress?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                          required
                        />
                      </div>

                      {/* Landmark */}
                      <div className="space-y-1">
                        <Label htmlFor="landmark" className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
                          Landmark (Optional)
                        </Label>
                        <Input
                          id="landmark"
                          placeholder="e.g. Near City Mall"
                          value={ticketLandmark}
                          onChange={(e) => handleAddressFieldChange('landmark', e.target.value)}
                          className="h-10 border-slate-300 text-xs"
                        />
                      </div>

                      {/* City */}
                      <div className="space-y-1">
                        <Label htmlFor="city" className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
                          City / District *
                        </Label>
                        <Input
                          id="city"
                          placeholder="e.g. Mumbai"
                          value={ticketCity}
                          onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                          className={`h-10 border-slate-300 text-xs ${step3SubmitAttempted && !ticketCity?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                          required
                        />
                      </div>

                      {/* State */}
                      <div className="space-y-1">
                        <Label htmlFor="state" className="text-[10px] font-bold uppercase tracking-wider text-slate-700">
                          State *
                        </Label>
                        <Input
                          id="state"
                          placeholder="e.g. Maharashtra"
                          value={ticketState}
                          onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                          className={`h-10 border-slate-300 text-xs ${step3SubmitAttempted && !ticketState?.trim() ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                          required
                        />
                      </div>
                    </div>

                    {/* Full Address Preview */}
                    <div className="pt-2.5 border-t">
                      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-700 block mb-1">
                        Full Service Address Preview
                      </Label>
                      <div className="p-2.5 bg-slate-50 border rounded-md text-xs text-slate-700 min-h-[38px] leading-relaxed">
                        {ticketServiceAddress || <span className="text-slate-400 italic">Combined address will be shown here...</span>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Review */}
              {ticketStep === 4 && (
                <div className="space-y-4 animate-fade-in">
                  <div className="border-b pb-3">
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Customer Details</Label>
                    <p className="font-semibold text-slate-800 text-sm">{customer?.name}</p>
                    <p className="text-xs text-muted-foreground">Phone: {customer?.phone}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Product Selected</Label>
                    <p className="font-semibold text-slate-800 text-sm">
                      {selectedProduct?.product_name || selectedProduct?.model_name}
                    </p>
                    <p className="text-xs text-muted-foreground">Serial: {selectedProduct?.serial_number}</p>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Selected Issues & Priority</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
                        ticketPriority === 'urgent'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : ticketPriority === 'low'
                          ? 'bg-slate-50 text-slate-700 border-slate-200'
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}>
                        Priority: {ticketPriority}
                      </span>
                      {selectedCategoryIds.map(id => {
                        const issue = issues?.find(i => i.id === id);
                        return issue ? (
                          <span key={id} className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-primary/10 text-primary border border-primary/20">
                            {issue.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Service Address</Label>
                    <p className="text-xs bg-slate-50 p-3 rounded-lg border border-slate-100 whitespace-pre-wrap text-slate-700 leading-relaxed">
                      {ticketServiceAddress}
                    </p>
                    {ticketLat && ticketLng && (
                      <p className="text-[10px] text-muted-foreground mt-1 font-medium">
                        GPS Coordinates: {ticketLat.toFixed(6)}, {ticketLng.toFixed(6)}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1 block">Detailed Description</Label>
                    <p className="text-xs bg-slate-50 p-3 rounded-lg border border-slate-100 whitespace-pre-wrap text-slate-700 leading-relaxed">
                      {ticketDescription}
                    </p>
                  </div>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
                {ticketStep > 1 && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setStep3SubmitAttempted(false); setTicketStep((prev) => prev - 1); }}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-5 border-slate-300"
                  >
                    Previous
                  </Button>
                )}
                {ticketStep === 1 && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowCreateTicket(false)}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-5 border-slate-300"
                  >
                    Cancel
                  </Button>
                )}
                
                {ticketStep < 4 ? (
                  <Button 
                    key="next-btn"
                    type="button"
                    onClick={handleNextStep}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white ml-auto"
                    style={{ backgroundColor: brandColorAccent }}
                  >
                    Next
                  </Button>
                ) : (
                  <Button 
                    key="submit-btn"
                    type="submit" 
                    disabled={submittingTicket}
                    className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white ml-auto"
                    style={{ backgroundColor: brandColorAccent }}
                  >
                    {submittingTicket ? 'Submitting...' : 'Create Ticket'}
                  </Button>
                )}
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
